import {
  Box,
  Button,
  Container,
  Divider,
  LinearProgress,
  List,
  ListItem,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNotification } from "app/NotificationProvider";
import {
  BottomSheetHeader,
  ConfirmCancellationDialog,
} from "common/components";
import { useTranslation } from "common/locales";
import { Form, Formik } from "formik";
import {
  useAnimalGroup,
  useCurrentFarmAnimalGroups,
} from "model/useAnimalGroupQueries";
import { useCurrentFarm } from "model/useFarmQueries";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createAnimalGroup, updateAnimalGroup } from "services";
import { useAppStore } from "stores";
import * as Yup from "yup";
import { analytics, AnalyticsEvents } from "../../common/analytics";

const GUID_TO_BE_CREATED = "GUID_TO_BE_CREATED";

export default function AnimalGroupInput() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const navigate = useNavigate();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const { data: currentFarm } = useCurrentFarm();
  const { isLoading } = useCurrentFarmAnimalGroups();

  const { animalGroupId } = useParams<{ animalGroupId: string }>();
  const { data: animalGroup } = useAnimalGroup(animalGroupId);

  const { notify } = useNotification();
  const nameInputRef = useRef<HTMLInputElement>(null);

  const AnimalGroupSchema = Yup.object().shape({
    name: Yup.string().required(t("edit.required.error")),
  });

  const [initialValues, setInitialValues] = useState({ name: "" });
  const initializedForId = useRef<string | null>(null);

  useEffect(() => {
    if (!animalGroupId) return;
    if (initializedForId.current === animalGroupId) return;
    if (!animalGroup) return;

    initializedForId.current = animalGroupId;
    setInitialValues({ name: animalGroup.name });
  }, [animalGroupId, animalGroup]);

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const handleBackToFarm = () => {
    if (currentFarm?.guid) {
      navigate(`/farm/${currentFarm.guid}`);
    }
  };

  async function handleSave(name: string) {
    try {
      if (animalGroup) {
        await updateAnimalGroup({ ...animalGroup, name });
        notify(t("animalGroupInput.edit.success", { animalGroupName: name }));
        analytics.track(AnalyticsEvents.ANIMAL_GROUP_UPDATED);
        navigate(`/animal-group/${animalGroup.guid}`);
      } else if (currentFarmId) {
        const newAnimalGroup = await createAnimalGroup({
          guid: GUID_TO_BE_CREATED,
          farm: currentFarmId,
          name,
          postGrazingTarget: 1500,
          rotationLength: 20,
          stockClasses: [],
        });
        analytics.track(AnalyticsEvents.ANIMAL_GROUP_CREATED);
        notify(t("animalGroupInput.add.success", { animalGroupName: name }));
        navigate(`/animal-group/${newAnimalGroup.guid}`);
      }
    } catch (e) {
      notify(t("animalGroupInput.boundary.error"), "error");
    }
  }

  if (isLoading) {
    return <LinearProgress />;
  }

  return (
    <Box
      sx={{
        backgroundColor: isMobile ? "white" : "transparent",
        minHeight: "100%",
        paddingBottom: isMobile ? "100px" : 0,
      }}
    >
      {isMobile && (
        <BottomSheetHeader
          title={
            animalGroup
              ? t("edit.editingAnimalGroup")
              : t("edit.addingAnimalGroup")
          }
          onBack={handleBackToFarm}
        />
      )}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={AnimalGroupSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(values) => {
          void handleSave(values.name);
        }}
      >
        {({ values, handleChange, errors, validateField }) => (
          <Container
            sx={{
              paddingTop: {
                xs: "calc(env(safe-area-inset-top) + 16px)",
                sm: "16px",
              },
              backgroundColor: isMobile ? "white" : "transparent",
            }}
          >
            <Form id="animal-group-input-form">
              <List>
                <ListItem>
                  <Field
                    required
                    id="animal-group-input-name"
                    label={t("edit.animalGroupName.label")}
                    inputRef={nameInputRef}
                    value={values.name}
                    helperText={errors.name}
                    error={!!errors.name}
                    fullWidth
                    onChange={async (
                      e: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      await (handleChange(e) as unknown as Promise<void>);
                      await (validateField("name") as unknown as Promise<void>);
                    }}
                    name="name"
                  />
                </ListItem>
              </List>
            </Form>
          </Container>
        )}
      </Formik>
      <Divider />
      <ButtonContainer>
        <ButtonGroup>
          <Button color="secondary" onClick={() => setCancelDialogOpen(true)}>
            {t("edit.button.cancel")}
          </Button>
          <Button color="primary" type="submit" form="animal-group-input-form">
            {t("edit.button.save")}
          </Button>
        </ButtonGroup>
        <ConfirmCancellationDialog
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
        />
      </ButtonContainer>
    </Box>
  );
}

const ButtonGroup = styled("div")(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(2),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const ButtonContainer = styled(Container)({
  position: "relative",
  flex: "0 0 auto",
  display: "block",
  height: "70px",
  width: "100%",
  background: "white",
});

const Field = styled(TextField)(({ theme }) => ({
  minWidth: "300px",
  paddingBottom: theme.spacing(2),
}));
